import { defineStore } from 'pinia';
import axios from 'axios';
import { useMainStore } from '@/stores/useMainStore';
import { useEmployeeStore } from '@/stores/useEmployeeStore';
import { reqHeaders } from '@/utils/fetches';
import { timestampNow, dateString, cleanDay } from '@/data/placeholder/date';
import { reportFatalError, reportNonFatalError, reportSilentError } from '@/utils/storeErrorReporting';
import { getMessagingTokenError, getLoadMessagesError, getSendMessageError, getCreateMessageStoreError } from '@/utils/errorMessages';

const API_DOMAIN = import.meta.env.VITE_API_URL;

export const useMessagingStore = defineStore('messaging', {
    state: () => ({}),

    getters: {
        /**
         * Returns the list of message-eligible requests
         * @returns {Array} Array of requests that can be messaged
         */
        messages: () => useMainStore().page.messages || [],

        /**
         * Returns parsed messages grouped by day
         * @returns {Object} Object with day keys containing display and messages arrays
         */
        messageHistory: () => useMainStore().page.message || {},
    },

    actions: {
        /**
         * Requests a token request object from the backend for Ably authentication
         * @param {Object} payload - Payload containing token for authorization
         * @param {string} payload.token - Authorization token
         * @returns {Object|undefined} Token request object for Ably, or undefined on error
         */
        async requestMessagingToken(payload) {
            let token;

            const headers = reqHeaders(payload);

            try {
                const response = await axios.get(`${API_DOMAIN}/messaging/token_request`, headers);
                token = response.data;
            } catch (error) {
                reportFatalError(error, getMessagingTokenError());
            }

            return token;
        },

        /**
         * Fetches messages for a chat, parses them, groups by day, and marks self messages
         * @param {Object} payload - Payload containing token and chat ID
         * @param {string} payload.token - Authorization token
         * @param {Object} payload.data - Data object
         * @param {string} payload.data.chatId - Chat ID to fetch messages for
         */
        async getMessages(payload) {
            const { chatId } = payload.data;
            const { headers } = reqHeaders(payload);

            try {
                const response = await axios.get(`${API_DOMAIN}/messaging/${chatId}`, { headers });
                let message = response.data && response.data.length > 0 ? response.data : [];

                const currentUserId = useEmployeeStore().userId;
                const parsed = {};

                message = message
                    .filter((each) => !!each.timeStamp)
                    .sort((a, b) => {
                        const aTime = parseInt(a.timeStamp, 10);
                        const bTime = parseInt(b.timeStamp, 10);
                        let result = 0;
                        if (aTime < bTime) {
                            result = -1;
                        } else if (aTime > bTime) {
                            result = 1;
                        }

                        return result;
                    });

                message.forEach((each) => {
                    let self = false;
                    let { timestamp } = each;

                    const daysStored = Object.keys(parsed);

                    if (each.userId === currentUserId) {
                        self = true;
                    }

                    if (!timestamp && each.timeStamp) {
                        timestamp = each.timeStamp;
                    }

                    if (!timestamp) {
                        return;
                    }

                    const day = cleanDay(timestamp);

                    const messageItem = {
                        ...each,
                        timestamp,
                        self,
                    };

                    if (daysStored.includes(day.toString())) {
                        parsed[day].messages.push(messageItem);
                        return;
                    }

                    parsed[day] = {
                        display: dateString(day),
                        messages: [messageItem],
                    };
                });

                useMainStore().updatePage({ message: parsed });
            } catch (error) {
                reportNonFatalError(error, getLoadMessagesError());
            }
        },

        /**
         * Sends a message to a chat
         * @param {Object} payload - Payload containing token, chat ID, and message
         * @param {string} payload.token - Authorization token
         * @param {Object} payload.data - Data object
         * @param {string} payload.data.chatId - Chat ID to send message to
         * @param {string} payload.data.message - Message content to send
         */
        async sendMessage(payload) {
            const { chatId, message } = payload.data;
            const { headers } = reqHeaders(payload);

            const timestamp = timestampNow();

            const body = {
                timeStamp: timestamp,
                message,
            };

            try {
                const response = await axios.post(`${API_DOMAIN}/messaging/${chatId}`, body, { headers });

                useMainStore().updatePage(response.data);
            } catch (error) {
                reportNonFatalError(error, getSendMessageError());
                throw error;
            }
        },

        /**
         * Creates a message store (chat room) on the backend
         * @param {Object} payload - Payload containing token and data
         * @param {string} payload.token - Authorization token
         * @param {Object} payload.data - Data object containing message store details
         */
        async createMessageStore(payload) {
            const { headers } = reqHeaders(payload);
            const { data } = payload;

            try {
                await axios.post(`${API_DOMAIN}/messaging`, data, { headers });
            } catch (error) {
                reportNonFatalError(error, getCreateMessageStoreError());
            }
        },

        /**
         * Exchanges token request for actual Ably token via Ably API
         * This is the second step in the Ably authentication flow
         *
         * NOTE: This method is currently UNUSED. The application uses Ably's `authCallback`
         * approach where the tokenRequest from `requestMessagingToken` is passed directly
         * to Ably, which handles token exchange internally. This method is kept for potential
         * future use if the authentication flow changes to handle token exchange manually.
         *
         * @param {Object} payload - Payload containing token request
         * @param {Object} payload.tokenRequest - Token request object from requestMessagingToken
         * @param {string} payload.tokenRequest.keyName - Ably key name for API request
         * @returns {string|undefined} Ably token string, or undefined on error
         */
        async getAblyToken(payload) {
            const { tokenRequest } = payload;

            let token;

            try {
                const response = await axios.post(`https://rest.ably.io/keys/${tokenRequest.keyName}/requestToken`, tokenRequest, {});
                token = response.data.token;
            } catch (error) {
                reportSilentError(error);
            }

            return token;
        },
    },
});
